<template>
    <transition
        enter-class="opacity-0"
        enter-active-class="transition-opacity duration-200"
        leave-to-class="opacity-0"
        leave-active-class="transition-opacity duration-200"
    >
        <Layout v-show="ready" container-class="flex flex-col justify-center min-h-screen py-10 sm:min-h-0 bg-primary">
            <div v-if="ready" class="w-full max-w-sm px-10 mx-auto">
                <div class="flex justify-center mb-8">
                    <img :src="config.service_logo" :alt="config.service_name" class="block w-auto h-12">
                </div>

                <h1 class="mb-6 text-lg font-semibold leading-tight text-center text-highlight-100">
                    {{ _('login_title') }}
                </h1>

                <SocialiteForm/>

                <form @submit.prevent="submit">
                    <div class="mb-4">
                        <UsernameField
                            v-if="canSignUpWithPhone"
                            :label="_('login_input_username_label')"
                            v-model="emailAddress"
                        />
                        <EmailField
                            v-else
                            :label="_('login_input_email_label')"
                            v-model="emailAddress"
                        />
                        <span v-if="hasValidation('username')" class="mt-2 text-xs text-error">
                            {{ apiValidation('username').text }}
                        </span>

                        <span v-if="validationErrors && validationStatus === 401"
                            class="mt-2 text-xs text-error">
                            {{ validationErrors }}

                            <router-link v-if="validationStatus === 401"
                                class="underline text-highlight-100"
                                :to="{ name: 'Register' }">
                                {{ _('login_create_account') }}
                            </router-link>
                        </span>
                    </div>

                    <div class="mb-8">
                        <PasswordField
                            :label="_('login_input_password_label')"
                            v-model="password"
                            :showTip="false"
                        />

                        <span v-if="hasValidation('password')" class="mt-2 text-xs text-error">
                            {{ apiValidation('password').text }}
                        </span>
                    </div>

                    <BaseButton type="button" class="py-2 mb-4 font-medium normal-case" :disabled="submitted">
                        {{ _('login_button_submit') }}
                    </BaseButton>
                </form>

                <BaseLink class="py-2 normal-case" :to="{name: 'Forgot'}">
                    {{ _('login_forgot_password') }}
                </BaseLink>

                <div v-if="registerEnabled">
                    <hr class="mt-4 mb-6 border-highlight-20">

                    <p class="mb-4 text-sm text-center text-highlight-100">{{ _('login_no_account')
                        }}</p>
                    <BaseButton class="py-2 font-medium normal-case" secondary to="/register">
                        {{ _('login_button_register') }}
                    </BaseButton>
                </div>
            </div>

            <Portal to="modal">
                <BaseModal :show="showModal">
                    <div class="w-full mt-3 text-center">
                        <h1 class="mb-6 text-lg font-semibold leading-tight text-center text-highlight-100">
                            {{ _('login_modal_title') }}
                        </h1>
                        <div class="mt-2">
                            <p class="text-sm leading-5 text-gray-500">
                                {{ validationErrors }}
                            </p>
                        </div>
                    </div>
                </BaseModal>
            </Portal>
        </Layout>
    </transition>
</template>

<script>
/* eslint-disable camelcase */
import NProgress from 'nprogress'
import { mapGetters } from 'vuex'
import Layout from '@/components/Layout.vue'
import BaseButton from '@/components/Base/BaseButton.vue'
import BaseModal from '@/components/Base/BaseModal.vue'
import BaseLink from '@/components/Base/BaseLink.vue'
import EmailField from '@/components/Form/Fields/EmailField.vue'
import UsernameField from '@/components/Form/Fields/UsernameField.vue'
import PasswordField from '@/components/Form/Fields/PasswordField.vue'
import ApiValidationHandler from '@/handlers/ApiValidationHandler'
import SocialiteForm from '@/components/Socialite/SocialiteForm.vue'

export default {
    components: {
        Layout,
        BaseButton,
        BaseLink,
        EmailField,
        UsernameField,
        PasswordField,
        BaseModal,
        SocialiteForm,
    },
    data() {
        return {
            apiValidationHandler: new ApiValidationHandler(),
            emailAddress: '',
            password: '',
            submitted: false,
            ready: false,
            config: '',
        }
    },
    computed: {
        ...mapGetters({
            fetched: 'localisation/fetched',
            _: 'localisation/translate',
            params: 'settings/get',
            authenticated: 'user/authenticated',
            client: 'settings/getClient',
            validationErrors: 'user/validationErrors',
            validationStatus: 'user/validationStatus',
            provider: 'providers/selected',
            previousUrl: 'route/getPreviousUrl',
            pack: 'packs/selected',
        }),
        apiValidation() {
            return (field) => this.apiValidationHandler.get(field)
        },
        hasValidation() {
            return (field) => this.apiValidationHandler.has(field)
        },
        showModal() {
            return this.validationStatus === 403 || this.validationStatus === 423
        },
        canSignUpWithPhone() {
            return process.env.VUE_APP_LOGIN_WITH_PHONE === 'true'
        },
        registerEnabled() {
            return process.env.VUE_APP_DISABLE_REGISTER !== this.params.platform;
        },
    },
    watch: {
        fetched: {
            immediate: true,
            deep: true,
            handler(fetched) {
                if (fetched) {
                    setTimeout(() => {
                        this.ready = true
                        NProgress.done()
                    }, 1000)
                }
            },
        },
        validationErrors: {
            immediate: true,
            deep: true,
            handler(errors) {
                this.apiValidationHandler.clear()

                if (this.validationStatus === 422) {
                    this.apiValidationHandler.record(errors)
                }
            },
        },
    },
    created() {
        NProgress.start()
    },
    async mounted() {
        this.$store.dispatch('user/resetErrors')
        this.config = await import(`@/resources/${this.client}`)
    },
    methods: {
        handleSuccess() {
            const user = this.authenticated
            const plan = this.pack

            const response = {
                ...user,
            }

            if (typeof plan !== 'undefined') {
                response.package = plan
            }

            if (typeof AndroidCallbacks !== 'undefined') {
                // eslint-disable-next-line no-undef
                AndroidCallbacks.userLoggedIn(JSON.stringify(response))
            }

            try {
                // eslint-disable-next-line no-undef
                webkit.messageHandlers.loginAction.postMessage(JSON.stringify(response));
            } catch (error) {
                // Not an iOS device
            }

            this.submitted = false

            if (this.previousUrl === null) {
                this.$router.push({ name: 'Continue' })
            } else {
                const routeName = this.previousUrl.name;

                this.$store.dispatch('route/removePreviousUrl')
                this.$router.push({ name: routeName })
            }
        },
        submit() {
            this.$store.dispatch('user/resetErrors')
            this.submitted = true
            this.$store
                .dispatch('user/login', {
                    username: this.emailAddress,
                    password: this.password,
                })
                .then(() => {
                    this.handleSuccess()
                })
                .catch(() => {
                    this.submitted = false
                })
        },
    },
}
</script>
